import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {SelectionCriteriaVersionService} from "../../service/api/selection-criteria-version.service";
import {
    VersionComparison,
    VersionDetail,
    VersionHistoryResponse,
    VersionSummary
} from "../../model/dto/api/selection-criteria-versioning.interface";
import {ConfirmationService, MessageService} from "primeng/api";

/**
 * Component for displaying and managing selection criteria version history.
 * Implements F-016: Selection Criteria Versioning feature.
 *
 * Features:
 * - Display version history with change summaries
 * - View version details and criteria snapshots
 * - Compare versions side by side
 * - Roll back to previous versions
 */
@Component({
    selector: 'ct-selection-criteria-version-history',
    templateUrl: './selection-criteria-version-history.component.html',
    styleUrls: ['./selection-criteria-version-history.component.scss'],
    standalone: false
})
export class SelectionCriteriaVersionHistoryComponent implements OnInit, OnChanges {

    @Input() selectionCriteriaId: number;
    @Input() visible: boolean = false;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onRollback = new EventEmitter<void>();

    versionHistory: VersionHistoryResponse;
    selectedVersion: VersionDetail;
    versionComparison: VersionComparison;

    loading: boolean = false;
    loadingDetail: boolean = false;
    loadingComparison: boolean = false;

    showDetailDialog: boolean = false;
    showCompareDialog: boolean = false;
    showRollbackDialog: boolean = false;

    compareFromVersion: number;
    compareToVersion: number;

    rollbackTargetVersion: VersionSummary;
    rollbackReason: string = '';

    constructor(
        private versionService: SelectionCriteriaVersionService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService
    ) {
    }

    ngOnInit(): void {
        if (this.selectionCriteriaId) {
            this.loadVersionHistory();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectionCriteriaId'] && this.selectionCriteriaId) {
            this.loadVersionHistory();
        }
        if (changes['visible'] && this.visible && this.selectionCriteriaId) {
            this.loadVersionHistory();
        }
    }

    loadVersionHistory(): void {
        if (!this.selectionCriteriaId) return;

        this.loading = true;
        this.versionService.getVersionHistory(this.selectionCriteriaId).subscribe({
            next: (history) => {
                this.versionHistory = history;
                this.loading = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Versionsverlauf konnte nicht geladen werden'
                });
                this.loading = false;
            }
        });
    }

    viewVersionDetail(version: VersionSummary): void {
        this.loadingDetail = true;
        this.showDetailDialog = true;

        this.versionService.getVersionDetail(version.id).subscribe({
            next: (detail) => {
                this.selectedVersion = detail;
                this.loadingDetail = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Versionsdetails konnten nicht geladen werden'
                });
                this.loadingDetail = false;
                this.showDetailDialog = false;
            }
        });
    }

    openCompareDialog(): void {
        if (this.versionHistory?.versions?.length >= 2) {
            this.compareFromVersion = this.versionHistory.versions[1].versionNumber;
            this.compareToVersion = this.versionHistory.versions[0].versionNumber;
            this.showCompareDialog = true;
        }
    }

    compareVersions(): void {
        if (!this.compareFromVersion || !this.compareToVersion) return;

        this.loadingComparison = true;
        this.versionService.compareVersions(
            this.selectionCriteriaId,
            this.compareFromVersion,
            this.compareToVersion
        ).subscribe({
            next: (comparison) => {
                this.versionComparison = comparison;
                this.loadingComparison = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Versionen konnten nicht verglichen werden'
                });
                this.loadingComparison = false;
            }
        });
    }

    confirmRollback(version: VersionSummary): void {
        this.rollbackTargetVersion = version;
        this.rollbackReason = '';
        this.showRollbackDialog = true;
    }

    executeRollback(): void {
        if (!this.rollbackTargetVersion) return;

        this.confirmationService.confirm({
            message: `Sind Sie sicher, dass Sie auf Version ${this.rollbackTargetVersion.versionNumber} zurücksetzen möchten? Es wird eine neue Version mit den vorherigen Kriterien erstellt.`,
            header: 'Zurücksetzung bestätigen',
            icon: 'fa-solid fa-history',
            acceptButtonStyleClass: 'p-button-warning',
            accept: () => {
                this.versionService.rollbackToVersion(this.selectionCriteriaId, {
                    targetVersionNumber: this.rollbackTargetVersion.versionNumber,
                    rollbackReason: this.rollbackReason
                }).subscribe({
                    next: (version) => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Erfolg',
                            detail: `Auf Version ${this.rollbackTargetVersion.versionNumber} zurückgesetzt`
                        });
                        this.showRollbackDialog = false;
                        this.loadVersionHistory();
                        this.onRollback.emit();
                    },
                    error: (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Fehler',
                            detail: 'Zurücksetzung auf Version fehlgeschlagen'
                        });
                    }
                });
            }
        });
    }

    activateVersion(version: VersionSummary): void {
        this.confirmationService.confirm({
            message: `Sind Sie sicher, dass Sie Version ${version.versionNumber} aktivieren möchten?`,
            header: 'Aktivierung bestätigen',
            icon: 'fa-solid fa-check-circle',
            accept: () => {
                this.versionService.activateVersion(version.id).subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Erfolg',
                            detail: `Version ${version.versionNumber} aktiviert`
                        });
                        this.loadVersionHistory();
                    },
                    error: (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Fehler',
                            detail: 'Version konnte nicht aktiviert werden'
                        });
                    }
                });
            }
        });
    }

    closeDialog(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    getChangeTypeIcon(changeType: string): string {
        switch (changeType) {
            case 'ADDED':
                return 'fa-solid fa-plus-circle text-green-500';
            case 'REMOVED':
                return 'fa-solid fa-minus-circle text-red-500';
            case 'MODIFIED':
                return 'fa-solid fa-edit text-yellow-500';
            default:
                return 'fa-solid fa-question-circle';
        }
    }

    getElementTypeIcon(elementType: string): string {
        switch (elementType) {
            case 'GROUP':
                return 'fa-solid fa-layer-group';
            case 'CONDITION':
                return 'fa-solid fa-filter';
            default:
                return 'fa-solid fa-circle';
        }
    }

    formatDate(date: Date): string {
        if (!date) return '-';
        return new Date(date).toLocaleString();
    }

    getVersionOptions(): {label: string, value: number}[] {
        if (!this.versionHistory?.versions) return [];
        return this.versionHistory.versions.map(v => ({
            label: `Version ${v.versionNumber}`,
            value: v.versionNumber
        }));
    }
}
